import React, { useState, useEffect } from "react";
import { FormStyles, TextStyles } from "styles/style";
import { useAuth } from "hooks/auth";
import { api } from "supabase/database/api.ts";
import DataTable from "./DataTable";

export default () => {
  const { user } = useAuth();
  const [scjCareerData, setScjCareerData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [staticData, setStaticData] = useState({
    networker: '',
    maintainer: '',
    bb_teacher: ''
  });
  const [formData, setFormData] = useState({
    group_ministry: '',
    duty: '',
    period: '',
    wod_in_charge: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const columns = [
    { key: 'group_ministry', header: 'Group/Ministry' },
    { key: 'duty', header: 'Duty' },
    { key: 'period', header: 'Period' },
    { key: 'wod_in_charge', header: 'WOD in Charge' }
  ];

  useEffect(() => {
    if (user?.email) {
      loadData();
    }
  }, [user?.email]);

  const loadData = async () => {
    try {
      const data = await api.dataQueries.GetUserData({ 
        table: 'scj_career_info', 
        email: user.email 
      });
      setScjCareerData(data);
      // Set static data from first entry if available
      if (data.length > 0) {
        setStaticData({
          networker: data[0].networker || '',
          maintainer: data[0].maintainer || '',
          bb_teacher: data[0].bb_teacher || ''
        });
      }
    } catch (error) {
      console.error("Error loading SCJ career data:", error);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAdd = () => {
    setFormData({
      group_ministry: '',
      duty: '',
      period: '',
      wod_in_charge: ''
    });
    setEditingIndex(null);
    setShowForm(true);
  };

  const handleEdit = (row, index) => {
    setFormData(row);
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingIndex(null);
    setFormData({
      group_ministry: '',
      duty: '',
      period: '',
      wod_in_charge: ''
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const dataWithStatic = { ...formData, ...staticData };
      
      if (editingIndex !== null) {
        await api.dataMutations.UpdateData({ 
          table: 'scj_career_info',
          id: scjCareerData[editingIndex].id, 
          data: dataWithStatic 
        });
      } else {
        await api.dataMutations.CreateData({ 
          table: 'scj_career_info',
          data: { ...dataWithStatic, user_email: user.email }
        });
      }
      
      alert("Shincheonji career information saved successfully!");
      setShowForm(false);
      loadData();
    } catch (error) {
      alert("Failed to save Shincheonji career information");
      console.error("Error saving SCJ career information:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showForm) {
    return (
      <FormStyles.FormContainer>
        <FormStyles.FormHeader>
          <FormStyles.FormTitle>
            {editingIndex !== null ? 'Edit' : 'Add'} Shincheonji Career Information
          </FormStyles.FormTitle>
          <FormStyles.FormBackButton onClick={handleCancel}>
            ← Back to List
          </FormStyles.FormBackButton>
        </FormStyles.FormHeader>
        
        <FormStyles.Form onSubmit={onSubmit}>
          <FormStyles.FormRow>
            <FormStyles.FormLabel>Group/Ministry</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={formData.group_ministry || ''}
              onChange={(e) => handleFieldChange("group_ministry", e.target.value)}
              placeholder="Enter group or ministry"
              required
            />
          </FormStyles.FormRow>

          <FormStyles.FormRow>
            <FormStyles.FormLabel>Duty</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={formData.duty || ''}
              onChange={(e) => handleFieldChange("duty", e.target.value)}
              placeholder="Enter duty/responsibility"
              required
            />
          </FormStyles.FormRow>

          <FormStyles.FormRow>
            <FormStyles.FormLabel>Period</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={formData.period || ''}
              onChange={(e) => handleFieldChange("period", e.target.value)}
              placeholder="e.g., Jan 2020 - Dec 2022"
              required
            />
          </FormStyles.FormRow>

          <FormStyles.FormRow>
            <FormStyles.FormLabel>WOD in Charge (at that time)</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={formData.wod_in_charge || ''}
              onChange={(e) => handleFieldChange("wod_in_charge", e.target.value)}
              placeholder="Enter leader at the time"
            />
          </FormStyles.FormRow>

          <FormStyles.FormButtonGroup>
            <FormStyles.FormSubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </FormStyles.FormSubmitButton>
            <FormStyles.FormCancelButton
              type="button"
              onClick={handleCancel}
            >
              Cancel
            </FormStyles.FormCancelButton>
          </FormStyles.FormButtonGroup>
        </FormStyles.Form>
      </FormStyles.FormContainer>
    );
  }

  return (
    <FormStyles.FormContainer>
      <FormStyles.FormStaticSection>
        <FormStyles.FormStaticGrid>
          <FormStyles.FormStaticField>
            <FormStyles.FormLabel>Networker</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={staticData.networker || ''}
              onChange={(e) => setStaticData(prev => ({ ...prev, networker: e.target.value }))}
              placeholder="Enter networker information"
            />
          </FormStyles.FormStaticField>
          <FormStyles.FormStaticField>
            <FormStyles.FormLabel>Maintainer</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={staticData.maintainer || ''}
              onChange={(e) => setStaticData(prev => ({ ...prev, maintainer: e.target.value }))}
              placeholder="Enter maintainer information"
            />
          </FormStyles.FormStaticField>
          <FormStyles.FormStaticField>
            <FormStyles.FormLabel>BB Teacher</FormStyles.FormLabel>
            <TextStyles.SearchInput
              type="text"
              value={staticData.bb_teacher || ''}
              onChange={(e) => setStaticData(prev => ({ ...prev, bb_teacher: e.target.value }))}
              placeholder="Enter BB teacher information"
            />
          </FormStyles.FormStaticField>
        </FormStyles.FormStaticGrid>
      </FormStyles.FormStaticSection>

      <DataTable
        data={scjCareerData}
        columns={columns}
        onEdit={handleEdit}
        onAdd={handleAdd}
        title="Ministry Positions"
      />
    </FormStyles.FormContainer>
  );
};